/**
 * Hash matchers for each top-level route. Each matcher takes a raw `window.location.hash`
 * and returns a flat `{ ...params }` on match, or `null` on no-match.
 *
 * Pure functions, no DOM, no module imports beyond URLSearchParams. Tested in
 * isolation by `matchers.test.ts`.
 */

const ID_RE = /^[a-z0-9-]+$/;
// base64url charset (RFC 4648 §5): A–Z, a–z, 0–9, '-', '_'. No padding.
const TOKEN_RE = /^[A-Za-z0-9_-]+$/;
const BLUEPRINT_PREFIX = '#/blueprint/';
const PATTERN_PREFIX = '#/p/';
const DESIGN_PREFIX = '#/design';
const PRIVACY_HASH = '#/privacy';

function splitQuery(rest: string): { path: string; query: URLSearchParams | null } {
  const q = rest.indexOf('?');
  if (q === -1) return { path: rest, query: null };
  return { path: rest.slice(0, q), query: new URLSearchParams(rest.slice(q + 1)) };
}

export function matchHome(hash: string): Record<string, string> | null {
  return hash === '#/' || hash === '' ? {} : null;
}

export function matchPrivacy(hash: string): Record<string, string> | null {
  return hash === PRIVACY_HASH ? {} : null;
}

export function matchDesign(hash: string): Record<string, string> | null {
  if (!hash.startsWith(DESIGN_PREFIX)) return null;
  const rest = hash.slice(DESIGN_PREFIX.length);
  if (rest !== '' && !rest.startsWith('?')) return null;
  const { query } = splitQuery(rest);
  const params: Record<string, string> = {};
  const from = query?.get('from');
  const swap = query?.get('swap');
  const pattern = query?.get('p');
  const size = query?.get('size');
  if (from && ID_RE.test(from)) params.from = from;
  if (swap) params.swap = swap;
  if (pattern && TOKEN_RE.test(pattern)) params.pattern = pattern;
  if (size && ID_RE.test(size)) params.size = size;
  return params;
}

export function matchPattern(hash: string): Record<string, string> | null {
  if (!hash.startsWith(PATTERN_PREFIX)) return null;
  const rest = hash.slice(PATTERN_PREFIX.length);
  // Pattern routes are token-only — no query suffix. A trailing query would
  // mean malformed input; reject so the router falls through to fallback.
  if (!TOKEN_RE.test(rest)) return null;
  return { pattern: rest };
}

export function matchBlueprint(hash: string): Record<string, string> | null {
  if (!hash.startsWith(BLUEPRINT_PREFIX)) return null;
  const { path, query } = splitQuery(hash.slice(BLUEPRINT_PREFIX.length));
  if (!ID_RE.test(path)) return null;
  const params: Record<string, string> = { id: path };
  const swap = query?.get('swap');
  const size = query?.get('size');
  if (swap) params.swap = swap;
  if (size && ID_RE.test(size)) params.size = size;
  return params;
}
